// What saving a line's pick tickets writes — on Approve, and on a line's first save (which keeps
// who is on it): the baseline on the line when it fits, else one copy per pick ticket as before.
// One function for both, so the two paths cannot drift.

import { asDay, formatDay, localToday } from "./days";
import type { LineView, PtView } from "./assemble";
import { TEAM_REVIEW_FIELDS, copyInput as sourceCopy, copyRecordInput, leadPt, lineRef } from "./assemble";
import type { BaselineCopy, BaselineField } from "./baseline";
import { NO_BASELINE, baselineEntry, baselineFields, encodeBaseline, isBaselineCopy } from "./baseline";
import type { LinePickTicketRecord, RoutingLineRecord, SourceRow } from "./types";

const shown = (v: unknown) => (v === null || v === undefined ? "" : (asDay(v) ?? String(v)));

/**
 * Whether a copy needs saving again. Not for `sourceLoadedAt` alone: it moves on every A2000 sync,
 * and saving every copy again each time would be one call each.
 */
export const copyDiffers = (a: Record<string, unknown>, b: Record<string, unknown>) =>
  Object.keys(b).some((k) => k !== "sourceLoadedAt" && shown(a[k]) !== shown(b[k]));

export interface CopyWrite {
  pickTicket: string;
  /** The record to update; null: create one. */
  existing: LinePickTicketRecord | null;
  input: Omit<LinePickTicketRecord, "id">;
}

export interface ApprovalPlan {
  /** The line's baseline fields: the text, or all "" when the line keeps copies instead. */
  fields: Record<BaselineField, string>;
  /** The baseline did not fit: every pick ticket is a copy. */
  fallback: boolean;
  /** Only the copies that need a call: new ones, and changed ones. */
  writes: CopyWrite[];
}

/**
 * What Admin → Import approves instead of the source's, for a workbook row decided UPDATED: the
 * file's QTY (the row's total), FIRST SHIP and CANCEL on the line's lead pick ticket (`leadPt`), the
 * others as the source has them — so the Worklist shows the line Updated, old → new.
 */
export interface ApprovalOverrides {
  qty?: number | null;
  start?: string | null;
  cancel?: string | null;
}

/**
 * `realCopy` is the page's latest `line-pick-ticket` record of a pick ticket (never a baseline
 * copy); a pick ticket that has one was placed by hand, and keeps it. `overrides`: Admin → Import's
 * UPDATED rows (`ApprovalOverrides`).
 */
export function approvalPlan(
  line: Pick<LineView, "pts">,
  lineId: string,
  realCopy: (pt: PtView) => LinePickTicketRecord | null,
  overrides?: ApprovalOverrides,
): ApprovalPlan {
  const live = line.pts.filter((p) => p.source);
  const lead = overrides ? leadPt(live) : undefined;
  const copyInput = (s: SourceRow, id: string) => {
    const c = sourceCopy(s, id);
    if (!lead || String(s.pick_ticket) !== lead.pickTicket) return c;
    return {
      ...c,
      ...(overrides?.qty !== undefined ? { qty: overrides.qty } : {}),
      ...(overrides?.start !== undefined ? { startDate: overrides.start } : {}),
      ...(overrides?.cancel !== undefined ? { cancelDate: overrides.cancel } : {}),
    };
  };
  // A pick ticket gone from A2000 stays in the baseline that held it: History's record of it.
  const goneHeld = line.pts.filter((p) => !p.source && p.copy && isBaselineCopy(p.copy));
  const byHand = live.filter((p) => realCopy(p));
  const rest = live.filter((p) => !realCopy(p));

  const entries: BaselineCopy[] = [
    ...rest.map((p) => baselineEntry(copyInput(p.source!, lineId))),
    ...goneHeld.map((p) => baselineEntry(copyRecordInput(p.copy!, lineId))),
  ];
  const fields = baselineFields(encodeBaseline(entries));

  const write = (p: PtView, input: Omit<LinePickTicketRecord, "id">): CopyWrite | null => {
    const existing = realCopy(p);
    if (existing && !copyDiffers(copyRecordInput(existing, lineId), input)) return null;
    return { pickTicket: p.pickTicket, existing, input };
  };
  const writes = (
    fields
      ? byHand.map((p) => write(p, copyInput(p.source!, lineId)))
      : [
          ...live.map((p) => write(p, copyInput(p.source!, lineId))),
          ...goneHeld.map((p) => write(p, copyRecordInput(p.copy!, lineId))),
        ]
  ).filter((w): w is CopyWrite => w !== null);
  return { fields: fields ?? NO_BASELINE, fallback: !fields, writes };
}

/**
 * What Approve keeps beside the baseline, in the same save: the line's READY DATE and SCHEDULED SHIP
 * DATE as it shows them (`TEAM_REVIEW_FIELDS`); either differing later makes it Updated.
 */
export const approvedDates = (line: Pick<LineView, "fields">) =>
  Object.fromEntries(
    Object.entries(TEAM_REVIEW_FIELDS).map(([f, approved]) => [
      approved,
      line.fields[f as keyof LineView["fields"]] || null,
    ]),
  ) as Pick<RoutingLineRecord, "approvedReadyDate" | "approvedSchedShip">;

/**
 * The first save of a line the move-over shows Approved (or Updated): its baseline is the copies
 * it came with — what was approved on the lines it came from — not A2000 now, so an Updated line
 * stays Updated, and a pick ticket that joined since stays joined. Null when they do not fit
 * (the line then saves with no baseline, and shows New).
 */
export function heldPlan(line: Pick<LineView, "pts">, lineId: string): Record<BaselineField, string> | null {
  const held = line.pts.filter((p) => p.copy).map((p) => baselineEntry(copyRecordInput(p.copy!, lineId)));
  return held.length ? baselineFields(encodeBaseline(held)) : null;
}

/**
 * Approve (item 4): the selected open lines in the WMS that are not Approved go; the Approved ones
 * are skipped. None to approve: the button is hidden.
 */
export const toApprove = (ls: readonly LineView[]): { done: LineView[]; todo: LineView[] } => {
  const can = ls.filter((l) => !l.noWms && !l.finished);
  return { done: can.filter((l) => l.review === "approved"), todo: can.filter((l) => l.review !== "approved") };
};

/** "TJ MAXX · WMS 37101 (by Isaac Shalita on 10/20/26)": who approved it and when, where the line says. */
export function approvedText(l: LineView): string {
  const name = [l.customerName || "—", lineRef(l)].filter(Boolean).join(" · ");
  const by = l.stored?.reviewedBy ?? "";
  const at = l.stored?.reviewedAt ? asDay(localToday(new Date(l.stored.reviewedAt))) : null;
  const when = [by ? `by ${by}` : "", at ? `on ${formatDay(at)}` : ""].filter(Boolean).join(" ");
  return when ? `${name} (${when})` : name;
}
